import { useQueryClient } from '@tanstack/react-query';
import { Button, Logo } from '@viaja/design-system';
import pattern from '@viaja/design-system/assets/padroes/horizonte-rota.svg';
import { type FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ApiError } from '../../lib/api';
import { useAuthConfig, useLogin, useSignup } from '../../lib/queries';
import { TextField } from '../../ui/TextField';
import { Notice } from '../../ui/feedback';
import { LoginPreloader } from './LoginPreloader';
import { useLoginTransition } from './RequireAuth';
import { preloadApp } from './preloadApp';

function errorsOf(err: unknown): { message: string | null; fields: Record<string, string> } {
  if (err instanceof ApiError) {
    const hasFields = Object.keys(err.fields).length > 0;
    return { message: hasFields ? null : err.message, fields: err.fields };
  }
  return { message: err ? 'Algo deu errado. Tente de novo.' : null, fields: {} };
}

function AuthFrame({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <main className="app-screen app-screen--bare app-auth">
      <div className="app-auth-brand">
        <Logo variant="vertical" height={190} />
      </div>
      <div className="app-pattern" style={{ backgroundImage: `url(${pattern})` }} aria-hidden="true" />
      <h1 className="display-title">{title}</h1>
      {children}
    </main>
  );
}

export function LoginPage() {
  const login = useLogin();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const holdTransition = useLoginTransition();
  const submitting = useRef(false);
  const completed = useRef(false);
  const [preparing, setPreparing] = useState(false);
  const [ready, setReady] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  useEffect(() => () => holdTransition(false), [holdTransition]);
  const { message, fields } = errorsOf(login.error);
  const requested = (location.state as { from?: string } | null)?.from;
  const destination =
    typeof requested === 'string' && requested.startsWith('/') && !requested.startsWith('//')
      ? requested
      : '/';
  const complete = useCallback(() => {
    if (completed.current) return;
    completed.current = true;
    navigate(destination, { replace: true });
  }, [navigate, destination]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (submitting.current) return;
    submitting.current = true;
    // Antes de mutate: useLogin atualiza a sessão e GuestOnly não pode desmontar o vídeo.
    holdTransition(true);
    login.mutate(
      { email, password },
      {
        onSuccess: () => {
          setPreparing(true);
          void preloadApp(queryClient, destination).finally(() => setReady(true));
        },
        onError: () => {
          submitting.current = false;
          holdTransition(false);
        },
      },
    );
  };

  return (
    <>
      <LoginPreloader active={preparing} ready={ready} onComplete={complete} />
      {!preparing ? (
        <AuthFrame title="Que bom te ver.">
          <form className="app-stack" onSubmit={submit} noValidate>
            <TextField
              label="E-mail"
              type="email"
              autoComplete="email"
              inputMode="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={fields.email}
            />
            <TextField
              label="Senha"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={fields.password}
            />
            {message ? <Notice>{message}</Notice> : null}
            <Button type="submit" block disabled={login.isPending}>
              {login.isPending ? 'Entrando…' : 'Entrar'}
            </Button>
          </form>
          <p className="small app-meta">
            Ainda não tem conta?{' '}
            <Link
              to="/cadastro"
              aria-disabled={login.isPending}
              onClick={(event) => {
                if (submitting.current) event.preventDefault();
              }}
            >
              Criar conta
            </Link>
          </p>
        </AuthFrame>
      ) : null}
    </>
  );
}

export function SignupPage() {
  const signup = useSignup();
  const config = useAuthConfig();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', password: '', inviteCode: '' });
  const { message, fields } = errorsOf(signup.error);
  const mode = config.data?.signup ?? 'invite';
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = (e: FormEvent) => {
    e.preventDefault();
    signup.mutate(
      {
        name: form.name,
        email: form.email,
        password: form.password,
        ...(mode === 'invite' ? { inviteCode: form.inviteCode } : {}),
      },
      { onSuccess: () => navigate('/', { replace: true }) },
    );
  };

  if (mode === 'closed') {
    return (
      <AuthFrame title="Cadastro fechado.">
        <p className="body app-meta">Por enquanto, só quem já tem conta consegue entrar.</p>
        <Link to="/entrar" className="small">
          Voltar para entrar
        </Link>
      </AuthFrame>
    );
  }

  return (
    <AuthFrame title="Vamos começar.">
      <form className="app-stack" onSubmit={submit} noValidate>
        <TextField
          label="Como te chamamos?"
          autoComplete="given-name"
          value={form.name}
          onChange={set('name')}
          error={fields.name}
        />
        <TextField
          label="E-mail"
          type="email"
          autoComplete="email"
          inputMode="email"
          value={form.email}
          onChange={set('email')}
          error={fields.email}
        />
        <TextField
          label="Senha"
          type="password"
          autoComplete="new-password"
          value={form.password}
          onChange={set('password')}
          error={fields.password}
          hint="Pelo menos 8 caracteres."
        />
        {mode === 'invite' ? (
          <TextField
            label="Código de convite"
            autoComplete="off"
            autoCapitalize="off"
            value={form.inviteCode}
            onChange={set('inviteCode')}
            error={fields.inviteCode}
          />
        ) : null}
        {message ? <Notice>{message}</Notice> : null}
        <Button type="submit" block disabled={signup.isPending}>
          {signup.isPending ? 'Criando conta…' : 'Criar conta'}
        </Button>
      </form>
      <p className="small app-meta">
        Já tem conta? <Link to="/entrar">Entrar</Link>
      </p>
    </AuthFrame>
  );
}
